import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLang } from '../contexts/LanguageContext';
import { serviceCategories, contact, ui } from '../data/content';

const PlaneAnimation = () => (
  <div style={{ position: 'relative', height: '30px', overflow: 'hidden', marginBottom: '8px' }}>
    <i className="fa-solid fa-plane-departure animate-plane-fly"
      style={{ position: 'absolute', color: '#FF9933', fontSize: '18px', bottom: '4px' }} />
  </div>
);

const CarAnimation = () => (
  <div style={{ position: 'relative', height: '26px', overflow: 'hidden', marginBottom: '8px' }}>
    <i className="fa-solid fa-car animate-car-drive"
      style={{ position: 'absolute', color: '#FF9933', fontSize: '16px', bottom: '4px' }} />
  </div>
);

const categoryColorMap = {
  government: { bg: 'rgba(255,215,0,0.06)', glow: '#FFD700', border: 'rgba(255,215,0,0.25)' },
  rto: { bg: 'rgba(255,153,0,0.06)', glow: '#FF9933', border: 'rgba(255,153,0,0.25)' },
  business: { bg: 'rgba(80,200,120,0.06)', glow: '#50C878', border: 'rgba(80,200,120,0.25)' },
  insurance: { bg: 'rgba(210,4,45,0.06)', glow: '#D2042D', border: 'rgba(210,4,45,0.25)' },
  travel: { bg: 'rgba(255,153,0,0.06)', glow: '#FF9933', border: 'rgba(255,153,0,0.25)' },
  digital: { bg: 'rgba(255,69,0,0.06)', glow: '#FF4500', border: 'rgba(255,69,0,0.25)' },
  video: { bg: 'rgba(210,4,45,0.06)', glow: '#D2042D', border: 'rgba(210,4,45,0.25)' },
  website: { bg: 'rgba(80,200,120,0.06)', glow: '#50C878', border: 'rgba(80,200,120,0.25)' },
};

function ServiceCategoryBlock({ cat, lang, index }) {
  const ref = useRef(null);
  const scheme = categoryColorMap[cat.id] || categoryColorMap.government;

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        entry.target.style.opacity = '1';
        entry.target.style.transform = 'translateY(0)';
      }
    }, { threshold: 0.1 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref}
      style={{ opacity: 0, transform: 'translateY(30px)', transition: 'opacity 0.7s ease, transform 0.7s ease' }}
      id={`service-${cat.id}`}>
      {/* Category header banner */}
      <div style={{
        background: `linear-gradient(135deg, ${scheme.bg.replace('0.06', '0.15')}, ${scheme.bg})`,
        border: `1px solid ${scheme.border}`,
        borderRadius: '16px', padding: '20px 28px', marginBottom: '20px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px',
        boxShadow: `0 0 30px ${scheme.glow}20, inset 0 0 30px ${scheme.glow}08`,
        position: 'relative', overflow: 'hidden',
      }}>
        {/* Decorative corner */}
        <div style={{ position: 'absolute', top: 0, right: 0, width: '80px', height: '80px', overflow: 'hidden', opacity: 0.2 }}>
          <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '80px', height: '80px', borderRadius: '50%', border: `2px solid ${scheme.glow}` }} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{
            width: '52px', height: '52px', borderRadius: '14px', flexShrink: 0,
            background: `${scheme.glow}18`, border: `1px solid ${scheme.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 0 15px ${scheme.glow}30`,
          }}>
            <i className={`fa-solid ${cat.iconClass}`} style={{ fontSize: '22px', color: scheme.glow, filter: `drop-shadow(0 0 6px ${scheme.glow})` }} />
          </div>
          <div>
            <h2 className="hindi" style={{ margin: 0, fontSize: 'clamp(18px,3vw,26px)', color: scheme.glow, fontFamily: '"Yatra One", cursive', fontWeight: 700, lineHeight: 1.1,
              textShadow: `0 0 15px ${scheme.glow}80` }}>
              {lang === 'hi' ? cat.titleHi : cat.titleEn}
            </h2>
            <p style={{ margin: '3px 0 0', fontSize: '13px', color: 'rgba(255,243,176,0.6)', fontFamily: 'Khand, sans-serif' }}>
              {lang === 'hi' ? cat.descHi : cat.descEn}
            </p>
          </div>
        </div>

        {/* Animated icon for travel/rto */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {cat.animated === 'plane' && (
            <div style={{ overflow: 'hidden', width: '80px', height: '28px', position: 'relative' }}>
              <i className="fa-solid fa-plane-departure animate-plane-fly"
                style={{ position: 'absolute', color: scheme.glow, fontSize: '18px', opacity: 0.8, bottom: '4px' }} />
            </div>
          )}
          {cat.animated === 'car' && (
            <div style={{ overflow: 'hidden', width: '80px', height: '26px', position: 'relative' }}>
              <i className="fa-solid fa-car animate-car-drive"
                style={{ position: 'absolute', color: scheme.glow, fontSize: '16px', opacity: 0.8, bottom: '3px' }} />
            </div>
          )}
          <a href={`https://wa.me/${contact.whatsapp}?text=मुझे ${lang === 'hi' ? cat.titleHi : cat.titleEn} सेवा के बारे में जानकारी चाहिए`}
            target="_blank" rel="noopener noreferrer"
            data-testid={`enquire-${cat.id}`}
            style={{
              background: `linear-gradient(135deg, ${scheme.glow}20, ${scheme.glow}08)`,
              border: `1px solid ${scheme.border}`,
              color: scheme.glow, padding: '8px 18px', borderRadius: '25px',
              textDecoration: 'none', fontFamily: 'Rajdhani, sans-serif', fontSize: '13px',
              fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap',
            }}>
            <i className="fa-brands fa-whatsapp" style={{ fontSize: '14px' }} />
            {ui.contact.enquiry[lang]}
          </a>
        </div>
      </div>

      {/* Services grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
        gap: '10px', marginBottom: '48px',
      }}>
        {cat.services.map((svc, i) => (
          <motion.a
            key={i}
            href={`https://wa.me/${contact.whatsapp}?text=मुझे ${lang === 'hi' ? svc.hi : svc.en} चाहिए`}
            target="_blank" rel="noopener noreferrer"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.04 }}
            data-testid={`service-${cat.id}-${i}`}
            className="service-item-pill"
            style={{
              padding: '10px 14px', borderRadius: '10px', textDecoration: 'none',
              display: 'flex', alignItems: 'center', gap: '8px',
            }}>
            <i className={`fa-solid ${cat.iconClass}`} style={{ fontSize: '11px', color: scheme.glow, flexShrink: 0 }} />
            <span style={{ fontSize: '13px', fontFamily: 'Khand, sans-serif', lineHeight: 1.3 }}>
              {lang === 'hi' ? svc.hi : svc.en}
            </span>
          </motion.a>
        ))}
      </div>
    </div>
  );
}

export default function ServicesSection() {
  const { lang } = useLang();

  return (
    <section id="services" data-testid="services-section"
      style={{ background: '#0A1128', padding: '80px 20px 40px', position: 'relative', overflow: 'hidden' }}>

      {/* Subtle background pattern */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0,
        backgroundImage: 'radial-gradient(circle at 30% 50%, rgba(255,215,0,0.02) 0%, transparent 50%), radial-gradient(circle at 70% 20%, rgba(255,153,0,0.02) 0%, transparent 50%)',
      }} />

      <div style={{ maxWidth: '1100px', margin: '0 auto', position: 'relative', zIndex: 1 }}>

        {/* Section heading */}
        <div style={{ textAlign: 'center', marginBottom: '56px' }}>
          <p style={{ fontFamily: 'Rajdhani, sans-serif', fontSize: '12px', letterSpacing: '5px', color: '#FF9933', textTransform: 'uppercase', marginBottom: '10px' }}>
            — Our Services —
          </p>
          <h2 className="hindi divine-glow-text"
            style={{ fontFamily: '"Yatra One", cursive', fontSize: 'clamp(26px,5vw,42px)', color: '#FFD700', margin: '0 0 12px' }}>
            {lang === 'hi' ? 'हमारी सेवाएँ' : 'Our Services'}
          </h2>
          <div className="section-divider">
            <span style={{ color: '#FFD700', fontSize: '18px' }}>✦</span>
            <span className="hindi-body" style={{ fontSize: '14px', color: 'rgba(255,243,176,0.6)', fontFamily: 'Khand, sans-serif' }}>
              {lang === 'hi' ? 'सभी सेवाएँ एक छत के नीचे' : 'All services under one roof'}
            </span>
            <span style={{ color: '#FFD700', fontSize: '18px' }}>✦</span>
          </div>
        </div>

        {/* All 8 service categories */}
        {serviceCategories.map((cat, i) => (
          <ServiceCategoryBlock key={cat.id} cat={cat} lang={lang} index={i} />
        ))}
      </div>
    </section>
  );
}
